import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { XIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import {
    ActionConfig,
    AlertRecipient,
    AlertRuleActionType,
    WebhookAuthorizationForm,
    WebhookHeader,
} from '@/types/alert';
import { ALERT_DEFAULT_CONFIDENCE_THRESHOLD } from '@/constants/alert';
import { Button } from '@/components/ui/Button';
import { NumberStepper, NumberStepperType, PhoneInput } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { cn } from '@/lib/utils';
import { POPOVER_SURFACE_CLASSES } from '@/components/ui/surface';
import { emptyAuthorization, isActionConfigured } from '../validation';
import { WebhookActionConfig } from './WebhookActionConfig';

const POPOVER_TITLE_KEYS: Record<AlertRuleActionType, string> = {
    [AlertRuleActionType.APP_NOTIFICATION]:
        'form.actionConfig.appNotification.popoverTitle',
    [AlertRuleActionType.WHATSAPP]: 'form.actionConfig.whatsapp.popoverTitle',
    [AlertRuleActionType.WEBHOOK]: 'form.actionConfig.webhooks.popoverTitle',
    [AlertRuleActionType.EMAIL]: 'form.actionConfig.email.popoverTitle',
};

interface ActionConfigPopoverProps {
    action: ActionConfig | null;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onSave: (action: ActionConfig) => void;
    onPreviewConfidenceThreshold?: (threshold: number | null) => void;
    children: React.ReactNode;
}

export function ActionConfigPopover({
    action,
    isOpen,
    onOpenChange,
    onSave,
    onPreviewConfidenceThreshold,
    children,
}: ActionConfigPopoverProps) {
    const { t } = useTranslation(I18nNamespace.ALERT_RULES);
    const [confidenceThreshold, setConfidenceThreshold] = useState(
        ALERT_DEFAULT_CONFIDENCE_THRESHOLD,
    );
    const [recipients, setRecipients] = useState<AlertRecipient[]>([
        {
            id: crypto.randomUUID(),
            phone: '',
            confidenceThreshold: ALERT_DEFAULT_CONFIDENCE_THRESHOLD,
        },
    ]);
    const [webhookUrl, setWebhookUrl] = useState('');
    const [webhookHeaders, setWebhookHeaders] = useState<WebhookHeader[]>([]);
    const [webhookAuthorization, setWebhookAuthorization] =
        useState<WebhookAuthorizationForm>(emptyAuthorization());

    useEffect(() => {
        if (action) {
            setConfidenceThreshold(action.confidenceThreshold);
            if (action.recipients && action.recipients.length > 0) {
                setRecipients(action.recipients);
            } else if (action.recipient?.phone) {
                setRecipients([
                    {
                        id: crypto.randomUUID(),
                        phone: action.recipient.phone,
                        confidenceThreshold: action.confidenceThreshold,
                    },
                ]);
            } else {
                setRecipients([
                    {
                        id: crypto.randomUUID(),
                        phone: '',
                        confidenceThreshold: ALERT_DEFAULT_CONFIDENCE_THRESHOLD,
                    },
                ]);
            }
            setWebhookUrl(action.webhook?.url ?? '');
            setWebhookHeaders(action.webhook?.headers ?? []);
            setWebhookAuthorization(
                action.webhook?.authorization ?? emptyAuthorization(),
            );
        }
    }, [action]);

    if (!action) return <>{children}</>;

    const updateAction = () => {
        const updatedAction: ActionConfig = {
            id: action.id,
            type: action.type,
            confidenceThreshold:
                action.type === AlertRuleActionType.WHATSAPP
                    ? recipients[0].confidenceThreshold
                    : confidenceThreshold,
            recipient:
                action.type === AlertRuleActionType.WHATSAPP
                    ? { phone: recipients[0]?.phone }
                    : undefined,
            recipients:
                action.type === AlertRuleActionType.WHATSAPP
                    ? recipients
                    : undefined,
            webhook:
                action.type === AlertRuleActionType.WEBHOOK
                    ? {
                          url: webhookUrl.trim(),
                          headers: webhookHeaders,
                          authorization: webhookAuthorization,
                      }
                    : undefined,
        };

        onSave(updatedAction);
    };

    const addRecipient = () => {
        setRecipients([
            ...recipients,
            {
                id: crypto.randomUUID(),
                phone: '',
                confidenceThreshold: ALERT_DEFAULT_CONFIDENCE_THRESHOLD,
            },
        ]);
    };

    const removeRecipient = (id: string) => {
        if (recipients.length > 1) {
            setRecipients(recipients.filter((r) => r.id !== id));
        }
    };

    const handleConfirm = () => {
        updateAction();
        onPreviewConfidenceThreshold?.(null);
        onOpenChange(false);
    };

    const resetToAction = () => {
        if (!action) return;
        setConfidenceThreshold(action.confidenceThreshold);
        if (action.recipients && action.recipients.length > 0) {
            setRecipients(action.recipients);
        } else if (action.recipient?.phone) {
            setRecipients([
                {
                    id: crypto.randomUUID(),
                    phone: action.recipient.phone,
                    confidenceThreshold: action.confidenceThreshold,
                },
            ]);
        } else {
            setRecipients([
                {
                    id: crypto.randomUUID(),
                    phone: '',
                    confidenceThreshold: ALERT_DEFAULT_CONFIDENCE_THRESHOLD,
                },
            ]);
        }
        setWebhookUrl(action.webhook?.url ?? '');
        setWebhookHeaders(action.webhook?.headers ?? []);
        setWebhookAuthorization(
            action.webhook?.authorization ?? emptyAuthorization(),
        );
    };

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            onPreviewConfidenceThreshold?.(null);
            resetToAction();
        }
        onOpenChange(open);
    };

    return (
        <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
            <Popover.Trigger asChild>{children}</Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    side="right"
                    sideOffset={16}
                    align="start"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    className={cn(
                        POPOVER_SURFACE_CLASSES,
                        'z-50 w-96 flex flex-col overflow-hidden max-h-[600px]',
                    )}
                    collisionPadding={20}
                >
                    <div className="flex items-center justify-between p-4 pb-3 flex-shrink-0">
                        <h3 className="text-sm-medium text-white-100">
                            {t(POPOVER_TITLE_KEYS[action.type])}
                        </h3>
                        <button
                            onClick={() => handleOpenChange(false)}
                            className="p-1 hover:bg-grey-500 rounded transition-colors"
                        >
                            <XIcon size={16} className="text-grey-300" />
                        </button>
                    </div>

                    <div className="flex flex-col overflow-y-auto scrollbar-grey-600 px-4">
                        {(action.type === AlertRuleActionType.WEBHOOK ||
                            action.type ===
                                AlertRuleActionType.APP_NOTIFICATION) && (
                            <div className="flex items-center gap-3 pb-4">
                                <span className="text-sm text-white-100 flex-1">
                                    {t('form.actionConfig.minConfidenceLevel')}
                                </span>
                                <NumberStepper
                                    value={confidenceThreshold}
                                    onChange={(val) => {
                                        setConfidenceThreshold(val);
                                        onPreviewConfidenceThreshold?.(val);
                                    }}
                                    type={NumberStepperType.PERCENTAGE}
                                    min={0}
                                    max={100}
                                    step={5}
                                />
                            </div>
                        )}

                        {action.type === AlertRuleActionType.WEBHOOK && (
                            <WebhookActionConfig
                                alertRuleActionId={action.id}
                                url={webhookUrl}
                                headers={webhookHeaders}
                                authorization={webhookAuthorization}
                                onUrlChange={setWebhookUrl}
                                onHeadersChange={setWebhookHeaders}
                                onAuthorizationChange={setWebhookAuthorization}
                            />
                        )}

                        {action.type === AlertRuleActionType.WHATSAPP && (
                            <div className="flex flex-col gap-3 pb-3">
                                {recipients.map((recipient) => (
                                    <div
                                        key={recipient.id}
                                        className="flex flex-col gap-2 p-3 border border-grey-500 rounded-[8px]"
                                    >
                                        <div className="flex items-center gap-2">
                                            {recipients.length > 1 && (
                                                <button
                                                    onClick={() =>
                                                        removeRecipient(
                                                            recipient.id,
                                                        )
                                                    }
                                                    className="p-1 hover:bg-grey-500 rounded transition-colors"
                                                >
                                                    <TrashIcon
                                                        size={16}
                                                        className="text-grey-300"
                                                    />
                                                </button>
                                            )}
                                            <PhoneInput
                                                value={recipient.phone}
                                                onChange={(val) => {
                                                    setRecipients(
                                                        recipients.map((r) =>
                                                            r.id ===
                                                            recipient.id
                                                                ? {
                                                                      ...r,
                                                                      phone: val,
                                                                  }
                                                                : r,
                                                        ),
                                                    );
                                                }}
                                                className="flex-1"
                                            />
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm text-white-100 flex-1">
                                                {t(
                                                    'form.actionConfig.minConfidenceLevel',
                                                )}
                                            </span>
                                            <NumberStepper
                                                value={
                                                    recipient.confidenceThreshold
                                                }
                                                onChange={(val) => {
                                                    const updated =
                                                        recipients.map((r) =>
                                                            r.id ===
                                                            recipient.id
                                                                ? {
                                                                      ...r,
                                                                      confidenceThreshold:
                                                                          val,
                                                                  }
                                                                : r,
                                                        );
                                                    setRecipients(updated);
                                                    const minThreshold =
                                                        Math.min(
                                                            ...updated.map(
                                                                (r) =>
                                                                    r.confidenceThreshold,
                                                            ),
                                                        );
                                                    onPreviewConfidenceThreshold?.(
                                                        minThreshold,
                                                    );
                                                }}
                                                type={
                                                    NumberStepperType.PERCENTAGE
                                                }
                                                min={0}
                                                max={100}
                                                step={5}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {action.type === AlertRuleActionType.WHATSAPP && (
                        <div className="px-4 pb-4 flex-shrink-0">
                            <Button
                                variant="ghost"
                                onClick={addRecipient}
                                leadingIcon={<PlusIcon size={16} />}
                                className="justify-start w-full"
                            >
                                {t('form.actionConfig.addRecipient')}
                            </Button>
                        </div>
                    )}

                    <div className="px-4 pb-4 pt-3 border-t border-grey-500 flex-shrink-0 flex justify-end gap-2">
                        <Button
                            variant="secondary"
                            onClick={() => handleOpenChange(false)}
                        >
                            {t('form.actionConfig.cancel')}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleConfirm}
                            disabled={
                                !isActionConfigured({
                                    ...action,
                                    webhook: {
                                        url: webhookUrl.trim(),
                                        headers: webhookHeaders,
                                        authorization: webhookAuthorization,
                                    },
                                })
                            }
                        >
                            {t('form.actionConfig.confirm')}
                        </Button>
                    </div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}
